import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { BarreSites } from '../components/ui/BarreSites';
import { TableauSatisfaction } from '../components/satisfaction/TableauSatisfaction';
import { ModaleReponse } from '../components/satisfaction/ModaleReponse';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import {
  LIBELLES_TYPE_QUESTION,
  texteNps,
  useEnregistrerModeleQuestionnaire,
  useModelesQuestionnaires,
  useResultatsQuestionnaires,
  type ModeleQuestionnaire,
  type QuestionQuestionnaire,
  type ResultatsQuestionnaire,
  type TypeQuestion,
} from '../lib/useQuestionnaires';

type Onglet = 'tableau' | 'resultats' | 'questionnaires';

const ONGLETS: readonly (readonly [Onglet, string])[] = [
  ['tableau', 'Tableau de bord'],
  ['resultats', 'Résultats par questionnaire'],
  ['questionnaires', 'Questionnaires'],
];

function ongletDe(valeur: string | null): Onglet {
  return ONGLETS.some(([cle]) => cle === valeur) ? (valeur as Onglet) : 'tableau';
}

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function Barre({ part }: { part: number }): JSX.Element {
  return (
    <div className="progress-track h-1.5 w-full overflow-hidden rounded-full">
      <div className="progress-bar h-full" style={{ width: `${Math.round(part * 100)}%` }} />
    </div>
  );
}

/**
 * Écran « Satisfaction » (CDC §8.2) : questionnaires envoyés d'office —
 * satisfaction à 2 et 6 mois et en fin de parcours, insertion à 3 et 6 mois
 * après l'examen. Le tableau de bord (TF-433) les agrège à chaud et à froid,
 * par formation, formateur et promotion ; « Résultats par questionnaire » en
 * donne le détail question par question ; « Questionnaires » leur contenu. Les
 * moyennes portent sur les réponses reçues ; un taux sans envoi reste vide. Les
 * réponses nommées et la composition sont réservées au pôle pédagogique et à la
 * direction : l'API le dit, l'écran suit. `?reponse=<id>` ouvre un
 * questionnaire répondu en entier, la cible des alertes de note basse (TF-434).
 */
export function SatisfactionPage(): JSX.Element {
  const { site } = usePerimetre();
  const [parametres, setParametres] = useSearchParams();
  const onglet = ongletDe(parametres.get('onglet'));
  const setOnglet = (cle: Onglet): void => setParametres({ onglet: cle }, { replace: true });
  // `?reponse=<id>` : la réponse qu'une alerte de note basse désigne (TF-434).
  const reponse = parametres.get('reponse');
  const voirReponse = (id: string | null): void =>
    setParametres(
      (courants) => {
        const suivants = new URLSearchParams(courants);
        if (id) suivants.set('reponse', id);
        else suivants.delete('reponse');
        return suivants;
      },
      { replace: true },
    );
  const resultats = useResultatsQuestionnaires(site, '', onglet === 'resultats');
  const modeles = useModelesQuestionnaires();
  const [edition, setEdition] = useState<ModeleQuestionnaire | null>(null);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Satisfaction et insertion"
        sousTitre="Questionnaires envoyés automatiquement · à chaud : satisfaction à 2 et 6 mois et en fin de parcours · à froid : insertion à 3 et 6 mois après l’examen"
      />

      <BarreSites />

      <nav className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-card" aria-label="Vues des questionnaires">
        {ONGLETS.map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            aria-current={onglet === cle ? 'page' : undefined}
            className={`min-h-[44px] min-w-0 flex-1 rounded-xl px-4 text-sm font-semibold transition sm:flex-none ${
              onglet === cle ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
            }`}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === 'tableau' && <TableauSatisfaction site={site} onVoirReponse={voirReponse} />}

      {onglet === 'resultats' && (
        <>
          {resultats.isLoading && <Chargement />}
          {resultats.error instanceof ErreurApi && <MessageErreur message={resultats.error.message} />}
          {resultats.data?.map((resultat) => <CarteResultat key={resultat.type} resultat={resultat} />)}
        </>
      )}

      {onglet === 'questionnaires' && (
        <Carte titre="Contenu des questionnaires">
          {modeles.isLoading && <Chargement />}
          {modeles.data?.modeles.map((modele) => (
            <div key={modele.type} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-codialis-navy">{modele.libelleType}</div>
                <div className="text-[11px] text-codialis-gray">
                  « {modele.titre} » · {modele.questions.length} question(s)
                  {modele.modifieLe
                    ? ` · modifié le ${new Date(modele.modifieLe).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })}${modele.modifiePar ? ` par ${modele.modifiePar}` : ''}`
                    : ''}
                </div>
              </div>
              {!modele.enregistre && <Pastille texte="Contenu proposé" ton="info" />}
              <button
                type="button"
                onClick={() => setEdition(modele)}
                className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[40px]"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                {modeles.data?.peutComposer ? 'Modifier' : 'Consulter'}
              </button>
            </div>
          ))}
        </Carte>
      )}

      {reponse && <ModaleReponse id={reponse} onFermer={() => voirReponse(null)} />}

      {edition && (
        <EditeurQuestionnaire
          modele={edition}
          lectureSeule={!modeles.data?.peutComposer}
          onFermer={() => setEdition(null)}
        />
      )}
    </div>
  );
}

function CarteResultat({ resultat }: { resultat: ResultatsQuestionnaire }): JSX.Element {
  const [detail, setDetail] = useState(false);
  return (
    <Carte
      titre={resultat.libelleType}
      actions={
        <span className="text-xs text-codialis-gray">
          {resultat.repondus}/{resultat.envoyes} réponse(s)
          {resultat.tauxReponse === null ? '' : ` · ${resultat.tauxReponse} %`}
        </span>
      }
      sansBordure
    >
      <div className="space-y-4 p-4 sm:p-6">
        {resultat.envoyes === 0 ? (
          <p className="text-sm text-codialis-gray">Aucun questionnaire de ce type n’a encore été envoyé.</p>
        ) : resultat.repondus === 0 ? (
          <p className="text-sm text-codialis-gray">Aucune réponse reçue pour l’instant.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {resultat.synthese.map((question) => (
              <div key={question.id} className="min-w-0 space-y-2 rounded-xl bg-gray-50 p-4">
                <div className="text-sm font-semibold text-codialis-navy">{question.libelle}</div>
                {question.type === 'NOTE' && (
                  <div className="space-y-1">
                    <div className="text-xs text-codialis-gray">
                      {question.moyenne === null ? 'Aucune note' : `${String(question.moyenne).replace('.', ',')} / 5`} ·{' '}
                      {question.repondants} réponse(s)
                    </div>
                    {question.moyenne !== null && <Barre part={question.moyenne / 5} />}
                  </div>
                )}
                {question.type === 'NPS' && question.nps && (
                  <div className="space-y-2">
                    <div className="text-xs text-codialis-gray">
                      NPS <span className="font-bold text-codialis-navy">{texteNps(question.nps.score)}</span> ·{' '}
                      {question.repondants} réponse(s)
                    </div>
                    {question.nps.parts && (
                      <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-codialis-gray">
                        <div>
                          <div className="text-sm font-bold text-codialis-navy">{question.nps.parts.promoteurs} %</div>
                          promoteurs (9-10)
                        </div>
                        <div>
                          <div className="text-sm font-bold text-codialis-navy">{question.nps.parts.passifs} %</div>
                          passifs (7-8)
                        </div>
                        <div>
                          <div className="text-sm font-bold text-codialis-navy">{question.nps.parts.detracteurs} %</div>
                          détracteurs (0-6)
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {(question.type === 'CHOIX_UNIQUE' || question.type === 'CHOIX_MULTIPLE') &&
                  question.repartition.map((choix) => (
                    <div key={choix.libelle} className="space-y-1">
                      <div className="flex justify-between gap-2 text-xs text-codialis-navy">
                        <span className="min-w-0 break-words">{choix.libelle}</span>
                        <span className="shrink-0 text-codialis-gray">{choix.nombre}</span>
                      </div>
                      <Barre part={question.repondants === 0 ? 0 : choix.nombre / question.repondants} />
                    </div>
                  ))}
                {question.type === 'TEXTE' &&
                  (question.textes.length === 0 ? (
                    <p className="text-xs text-codialis-gray">Aucune réponse libre.</p>
                  ) : (
                    <ul className="space-y-1">
                      {question.textes.slice(0, 5).map((texte, rang) => (
                        <li key={rang} className="break-words text-xs text-codialis-navy">
                          « {texte} »
                        </li>
                      ))}
                      {question.textes.length > 5 && (
                        <li className="text-[11px] text-codialis-gray">et {question.textes.length - 5} autre(s)</li>
                      )}
                    </ul>
                  ))}
              </div>
            ))}
          </div>
        )}

        {resultat.detail && resultat.detail.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setDetail((ouvert) => !ouvert)}
              className="min-h-[40px] text-xs font-semibold text-codialis-sky hover:underline"
            >
              {detail ? 'Masquer les répondants' : `Voir les ${resultat.detail.length} répondant(s)`}
            </button>
            {detail && (
              <ul className="mt-2 divide-y divide-gray-100 text-xs">
                {resultat.detail.map((ligne, rang) => (
                  <li key={rang} className="flex flex-wrap justify-between gap-2 py-2 text-codialis-navy">
                    <span>
                      {ligne.apprenant} · {ligne.siteCode}
                      {ligne.formation ? ` · ${ligne.formation}` : ''}
                    </span>
                    <span className="text-codialis-gray">{ligne.repondLe.split('-').reverse().join('/')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Carte>
  );
}

const CLASSE_ICONE =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy disabled:opacity-30';

/**
 * Contenu d'un questionnaire. Les envois déjà partis gardent leurs questions :
 * le modifier ne change que les prochains.
 */
function EditeurQuestionnaire({
  modele,
  lectureSeule,
  onFermer,
}: {
  modele: ModeleQuestionnaire;
  lectureSeule: boolean;
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const enregistrer = useEnregistrerModeleQuestionnaire();
  const [titre, setTitre] = useState(modele.titre);
  const [introduction, setIntroduction] = useState(modele.introduction);
  const [questions, setQuestions] = useState<QuestionQuestionnaire[]>(modele.questions);

  const modifier = (rang: number, valeurs: Partial<QuestionQuestionnaire>): void =>
    setQuestions((liste) => liste.map((question, index) => (index === rang ? { ...question, ...valeurs } : question)));
  const deplacer = (rang: number, sens: -1 | 1): void =>
    setQuestions((liste) => {
      const copie = [...liste];
      const [question] = copie.splice(rang, 1);
      if (question) copie.splice(rang + sens, 0, question);
      return copie;
    });

  return (
    <Modale titre={modele.libelleType} sousTitre="Les questionnaires déjà envoyés gardent leurs questions" ouverte onFermer={onFermer} largeur="max-w-3xl">
      <fieldset disabled={lectureSeule} className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Titre</span>
          <input value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={150} className={CLASSE_CHAMP} />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Introduction</span>
          <textarea value={introduction} onChange={(e) => setIntroduction(e.target.value)} rows={2} maxLength={2000} className={`${CLASSE_CHAMP} py-2`} />
        </label>

        <ol className="space-y-3">
          {questions.map((question, rang) => (
            <li key={question.id ?? `nouvelle-${rang}`} className="space-y-2 rounded-2xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-codialis-navy">Question {rang + 1}</span>
                <select
                  value={question.type}
                  onChange={(e) => {
                    const type = e.target.value as TypeQuestion;
                    const aChoix = type === 'CHOIX_UNIQUE' || type === 'CHOIX_MULTIPLE';
                    modifier(rang, {
                      type,
                      choix: aChoix ? (question.choix.length >= 2 ? question.choix : [{ libelle: '' }, { libelle: '' }]) : [],
                    });
                  }}
                  aria-label={`Type de la question ${rang + 1}`}
                  className="min-h-[40px] rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs text-codialis-navy"
                >
                  {/* La recommandation de 0 à 10 fait le NPS, qui se lit à chaud : l'API la refuse ailleurs. */}
                  {Object.entries(LIBELLES_TYPE_QUESTION)
                    .filter(([valeur]) => valeur !== 'NPS' || modele.type.startsWith('SATISFACTION') || question.type === 'NPS')
                    .map(([valeur, libelle]) => (
                      <option key={valeur} value={valeur}>
                        {libelle}
                      </option>
                    ))}
                </select>
                {!lectureSeule && (
                  <div className="ml-auto flex">
                    <button type="button" onClick={() => deplacer(rang, -1)} disabled={rang === 0} className={CLASSE_ICONE} aria-label={`Monter la question ${rang + 1}`}>
                      <ArrowUp className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" onClick={() => deplacer(rang, 1)} disabled={rang === questions.length - 1} className={CLASSE_ICONE} aria-label={`Descendre la question ${rang + 1}`}>
                      <ArrowDown className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" onClick={() => setQuestions((liste) => liste.filter((_, index) => index !== rang))} disabled={questions.length === 1} className={CLASSE_ICONE} aria-label={`Supprimer la question ${rang + 1}`}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
              <input
                value={question.libelle}
                onChange={(e) => modifier(rang, { libelle: e.target.value })}
                maxLength={500}
                aria-label={`Libellé de la question ${rang + 1}`}
                className={CLASSE_CHAMP}
              />
              {(question.type === 'CHOIX_UNIQUE' || question.type === 'CHOIX_MULTIPLE') && (
                <div className="space-y-2">
                  {question.choix.map((choix, index) => (
                    <div key={choix.id ?? `choix-${index}`} className="flex min-w-0 items-center gap-2">
                      <input
                        aria-label={`Libellé du choix ${index + 1}`}
                        value={choix.libelle}
                        onChange={(e) =>
                          modifier(rang, {
                            choix: question.choix.map((autre, position) => (position === index ? { ...autre, libelle: e.target.value } : autre)),
                          })
                        }
                        placeholder={`Choix ${index + 1}`}
                        maxLength={200}
                        className={`${CLASSE_CHAMP} min-w-0 flex-1`}
                      />
                      {!lectureSeule && (
                        <button
                          type="button"
                          onClick={() => modifier(rang, { choix: question.choix.filter((_, position) => position !== index) })}
                          disabled={question.choix.length <= 2}
                          className={CLASSE_ICONE}
                          aria-label={`Retirer le choix ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  ))}
                  {!lectureSeule && question.choix.length < 10 && (
                    <button
                      type="button"
                      onClick={() => modifier(rang, { choix: [...question.choix, { libelle: '' }] })}
                      className="inline-flex min-h-[40px] items-center gap-1.5 text-xs font-semibold text-codialis-sky hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                      Ajouter un choix
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>

        {!lectureSeule && (
          <BoutonSecondaire onClick={() => setQuestions((liste) => [...liste, { libelle: '', type: 'NOTE', choix: [] }])}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Ajouter une question
          </BoutonSecondaire>
        )}

        {enregistrer.error instanceof ErreurApi && <MessageErreur message={enregistrer.error.message} />}
      </fieldset>

      <div className="flex flex-wrap gap-2 border-t border-gray-100 p-4 sm:px-6">
        {!lectureSeule && (
          <BoutonPrincipal
            onClick={() =>
              enregistrer.mutate(
                { type: modele.type, titre, introduction, questions },
                {
                  onSuccess: () => {
                    notifier('Questionnaire enregistré : il sert aux prochains envois.');
                    onFermer();
                  },
                },
              )
            }
            disabled={enregistrer.isPending || titre.trim().length < 3}
          >
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer le questionnaire'}
          </BoutonPrincipal>
        )}
        <BoutonSecondaire onClick={onFermer} disabled={enregistrer.isPending}>
          Fermer
        </BoutonSecondaire>
      </div>
    </Modale>
  );
}
